import React, { useEffect } from "react";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import ButtonWithLoading from "../../../components/common/ButtonWithLoading";
import FormDatePicker from "../../../components/formFields/FormDateField";
import moment from "moment";
import { Typography } from "@progress/kendo-react-common";
import ShadowCard from "../../../components/common/ShadowCard";
import {
  clearPurchaseDueDateReportDetails,
  clearPurchaseDueDateReportList,
  setPurchaseDueDateReport,
} from "../reportsSlice";
import {
  Grid,
  GridColumn,
  GridCustomCellProps,
  GridCustomHeaderCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from "@progress/kendo-react-grid";
import {
  CompositeFilterDescriptor,
  filterBy,
} from "@progress/kendo-data-query";
import {
  HeaderThElement,
  PagerTargetEvent,
} from "@progress/kendo-react-data-tools";
import AlertBox from "../../../components/common/AlertBox";
import { LoadingPanel } from "../../../components/layout/Loading";
import { requiredValidator } from "../../../components/formFields/CommonValidator";
import { Button } from "@progress/kendo-react-buttons";
import Logo from "../../../assets/Images/logo.png";
import { checkAcessRights, formatIndianNumber } from "../../../_helper/helper";
import { EXCEL_EXPORT_ACCESS, PRINT_ACCESS } from "../../../_contstants/common";
import { getPurchaseDueDateInvoiceReport } from "../services/reports.services";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

interface FormChangeWatcherProps {
  formRenderProps: FormRenderProps;
}
const DatechangeWatcher: React.FC<FormChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const fromdate = moment()?.startOf("month")?.toDate() || "";
  const todate = moment()?.toDate() || "";
  useEffect(() => {
    formRenderProps.onChange("from_date", {
      value: fromdate,
    });
    formRenderProps.onChange("to_date", {
      value: todate,
    });
  }, []);

  return null;
};

const PurchaseDueDateInvoiceReportForm: React.FC = () => {
  const gridRef = React.useRef<any>(null);
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.reports.loading);
  const PurchaseDueDateReportList = useAppSelector(
    (state) => state.reports.PurchaseDueDateReportList
  );
  const PurchaseDueDateReportDetails = useAppSelector(
    (state) => state.reports.PurchaseDueDateReportDetails
  );

  useEffect(() => {
    return () => {
      dispatch(clearPurchaseDueDateReportList());
      dispatch(clearPurchaseDueDateReportDetails());
    };
  }, []);

  const handleSubmit = (values: any) => {
    const payload = {
      from_date: values?.from_date
        ? moment(values?.from_date).format("YYYY-MM-DD")
        : null,
      to_date: values?.to_date
        ? moment(values?.to_date).format("YYYY-MM-DD")
        : null,
    };
    dispatch(setPurchaseDueDateReport(payload));
    dispatch(getPurchaseDueDateInvoiceReport(payload));
  };
  return (
    <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
      <GridLayoutItem>
        <ShadowCard>
          <Form
            initialValues={PurchaseDueDateReportDetails}
            onSubmit={handleSubmit}
            render={(formRenderProps: FormRenderProps) => (
              <FormElement style={{ width: "100%" }}>
                <GridLayout
                  style={{ marginRight: 10 }}
                  gap={{ rows: 10, cols: 10 }}
                  cols={[
                    { width: "30%" },
                    { width: "30%" },
                    { width: "30%" },
                    { width: "10%" },
                  ]}
                >
                  <GridLayoutItem colSpan={4}>
                    <Typography.h4
                      style={{ marginBottom: 0, marginLeft: "3px" }}
                    >
                      Purchase Due Date Report
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="from_date"
                      label="From Date"
                      format="dd/MM/yyyy"
                      component={FormDatePicker}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="to_date"
                      label="To Date"
                      format="dd/MM/yyyy"
                      minDate={moment(
                        formRenderProps.valueGetter("from_date")
                      ).toDate() || new Date(1900, 0, 1)}
                      component={FormDatePicker}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem style={{ marginTop: 34 }}>
                    <ButtonWithLoading
                      label={"View"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                  </GridLayoutItem>
                </GridLayout>
                <DatechangeWatcher formRenderProps={formRenderProps} />
              </FormElement>
            )}
          />
        </ShadowCard>
        {loading ? (
          <LoadingPanel gridRef={gridRef} />
        ) : (
          PurchaseDueDateReportList &&
          PurchaseDueDateReportList.length > 0 && (
            <ShadowCard style={{ marginTop: 10 }}>
              <PurchaseDueDateGridCommponent />
            </ShadowCard>
          )
        )}
      </GridLayoutItem>
    </GridLayout>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 50 };

const PurchaseDueDateGridCommponent: React.FC = () => {
  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);
  const isExcelAccess = checkAcessRights(
    location.pathname,
    EXCEL_EXPORT_ACCESS
  );

  //   const loading = useAppSelector((state) => state.reports.loading);
  const PurchaseDueDateReportList = useAppSelector(
    (state) => state.reports.PurchaseDueDateReportList
  );
  const PurchaseDueDateReportDetails = useAppSelector(
    (state) => state.reports.PurchaseDueDateReportDetails
  );

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All"
        ? PurchaseDueDateReportList.length
        : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };

  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className="table-header"
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  const handleExportExcel = () => {
    const datetime = moment().format("DD-MM-YYYY HH-mm");
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.table_to_sheet(
      document.querySelector(".table-bordered")
    );
    XLSX.utils.book_append_sheet(wb, ws, "PurchaseDueDateReport");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(data, `Purchase Due Date Report ${datetime}`);
  };

  const printTable = (data: any[]) => {
    let printbuttons = document.querySelector(
      ".printbuttons"
    ) as HTMLElement | null;
    let report_container = document.querySelector(
      ".report-container"
    ) as HTMLElement | null;

    if (printbuttons) printbuttons.style.display = "none";
    if (report_container) report_container.style.padding = "20px 0";

    let tableRows = data.length;
    let maxRows = tableRows;
    let pageCount = Math.ceil(tableRows / maxRows);
    let content = "";

    for (let i = 0; i < pageCount; i++) {
      content += '<div class="print-page">';
      //   content += `<div class="page-header">Page ${i + 1}</div>`;
      content +=
        '<table class="table table-bordered" style="font-size:10px;margin-bottom:30px;">';
      content += "<tr>";
      content += `<td rowSpan="3" style="width:15%" ><img src="${Logo}" width="100%" /></td>`;
      content +=
        '<td colSpan="2" style="text-align: center;font-size: 14px;font-weight: 600;color: gray;">Harihar Foods Pvt Ltd.</td>';
      content += "</tr>";
      content += "<tr>";
      content +=
        '<td colSpan="2"><h3 style="margin:0;">Purchase Due Date Report</h3></td>';
      content += "</tr>";
      content += "<tr>";
      content += `<td style="text-align:end;">Page : ${i + 1}</td>`;
      content += "</tr>";

      content += "</table>";

      content += '<table class="table table-bordered" style="font-size:10px;">';
      content += '<thead style="background:lightgray;">';
      content += "<tr>";
      content += "<th>Sr. No.</th>";
      content += "<th>Po No</th>";
      content += "<th>Invoice No</th>";
      content += "<th>Invoice Date</th>";
      content += "<th>Account Name</th>";
      content += "<th>Terms Days</th>";
      content += "<th>Due Date</th>";
      content += "<th>Place Of Supply</th>";
      content += "<th>Total GST</th>";
      content += "<th>Total Amount</th>";
      content += "</tr>";
      content += "</thead>";
      content += "<tbody>";

      let startRow = i * maxRows;
      let endRow = Math.min((i + 1) * maxRows, tableRows);

      for (let j = startRow; j < endRow; j++) {
        content += `<tr style="text-align:center;">`;
        content += `<td>${j + 1}</td>`;
        content += `<td>${data[j].po_no}</td>`;
        content += `<td>${data[j].invoice_no}</td>`;
        content += `<td>${
          data[j]?.invoice_date
            ? moment(data[j]?.invoice_date, "DD/MM/YYYY").format("DD MMM YYYY")
            : ""
        }</td>`;
        content += `<td style="text-align:left;">${data[j].account_name}</td>`;
        content += `<td style="text-align:end;">${data[j].terms_days}</td>`;
        content += `<td>${
          data[j]?.due_date
            ? moment(data[j]?.due_date, "DD/MM/YYYY").format("DD MMM YYYY")
            : ""
        }</td>`;
        content += `<td style="text-align:left;">${data[j].place_of_supply_name}</td>`;
        content += `<td style="text-align:end;">${
          data[j].gst ? formatIndianNumber(data[j].gst) : null
        }</td>`;
        content += `<td style="text-align:end;">${
          data[j].total_amount ? formatIndianNumber(data[j].total_amount) : null
        }</td>`;
        content += "</tr>";
      }

      content += "</tbody>";
      content += "</table>";
      content += "</div>";
    }

    let printWindow = window.open("", "_blank");
    if (!printWindow) {
      console.error("Failed to open print preview window.");
      return;
    }
    printWindow.document.write(`
      <html>
      <head>
        <title>Print Preview</title>
        <style>
          @page { margin: 1cm; }
          .print-page { page-break-after: always; }
          .table-bordered { width: 100%; border-collapse: collapse; }
          .table-bordered th, .table-bordered td { border: 1px solid black; padding: 8px; }
          body { font-family: Arial, sans-serif; }
        </style>
      </head>
      <body>
        ${content}
        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();

    if (printbuttons) printbuttons.style.display = "block";
    if (report_container) report_container.style.padding = "20px 10px";
  };

  return (
    <>
      {PurchaseDueDateReportList && PurchaseDueDateReportList.length ? (
        <GridLayout
          //   style={{ marginRight: 30 }}
          gap={{ rows: 10, cols: 10 }}
          cols={[{ width: "100%" }]}
        >
          <GridLayoutItem className="d-flex justify-content-between align-items-center">
            <Typography.h4 className="m-0">
              Purchase Due Date Report List{" "}
              {(PurchaseDueDateReportDetails?.from_date ||
                PurchaseDueDateReportDetails?.to_date) && (
                <span style={{ fontSize: 13 }}>
                  {"("}
                  {PurchaseDueDateReportDetails?.from_date
                    ? moment(
                        PurchaseDueDateReportDetails?.from_date,
                        "YYYY-MM-DD"
                      ).format("DD/MM/YYYY")
                    : ""}
                  {PurchaseDueDateReportDetails?.from_date &&
                    PurchaseDueDateReportDetails?.to_date &&
                    " - "}
                  {PurchaseDueDateReportDetails?.to_date
                    ? moment(
                        PurchaseDueDateReportDetails?.to_date,
                        "YYYY-MM-DD"
                      ).format("DD/MM/YYYY")
                    : ""}
                  {")"}
                </span>
              )}
            </Typography.h4>
            <div>
              {isExcelAccess && (
                <Button
                  style={{ marginRight: 5 }}
                  type="button"
                  fillMode={"solid"}
                  themeColor={"primary"}
                  onClick={handleExportExcel}
                >
                  Excel Export
                </Button>
              )}
              {isPrintAccess && (
                <Button
                  style={{ marginRight: 5 }}
                  type="button"
                  fillMode={"solid"}
                  themeColor={"primary"}
                  onClick={() => printTable(PurchaseDueDateReportList)}
                >
                  Print
                </Button>
              )}
            </div>
          </GridLayoutItem>
          <GridLayoutItem>
            <Grid
              className="table-bordered responsive-table"
              style={{ maxHeight: "calc(100vh - 152px)" }}
              filterable={true}
              filter={filter}
              data={
                filter
                  ? filterBy(PurchaseDueDateReportList, filter).slice(
                      page.skip,
                      page.take + page.skip
                    )
                  : PurchaseDueDateReportList.slice(
                      page.skip,
                      page.take + page.skip
                    )
              }
              onFilterChange={handleFilterChange}
              skip={page.skip}
              take={page.take}
              total={PurchaseDueDateReportList.length}
              pageable={{
                buttonCount: 5,
                pageSizes: [10, 25, 50, 100, 500, "All"],
                pageSizeValue: pageSizeValue,
                type: "input",
              }}
              onPageChange={pageChange}
              cells={{
                headerCell: HeaderCustomCell,
                data: MyDataCustomCell,
              }}
            >
              <GridColumn
                field="UniqueNo"
                title="Sr. No."
                width={80}
                filterable={false}
              />
              <GridColumn field="po_no" title="PO No" width={150} />
              <GridColumn field="invoice_no" title="Invoice No" width={150} />
              <GridColumn
                field="invoice_date"
                title="Invoice Date"
                width={200}
                cell={(props: any) => (
                  <td>
                    {props.dataItem?.invoice_date
                      ? moment(
                          props.dataItem?.invoice_date,
                          "DD/MM/YYYY"
                        ).format("DD MMM YYYY")
                      : ""}
                  </td>
                )}
              />
              <GridColumn
                field="account_name"
                title="Account Name"
                width={250}
              />
              <GridColumn
                field="terms_days"
                title="Terms Day"
                width={150}
                cell={(props: any) => (
                  <td style={{ textAlign: "end" }}>
                    {props.dataItem?.terms_days}
                  </td>
                )}
              />
              <GridColumn
                field="due_date"
                title="Due Date"
                width={200}
                cell={(props: any) => (
                  <td>
                    {props.dataItem?.due_date
                      ? moment(props.dataItem?.due_date, "DD/MM/YYYY").format(
                          "DD MMM YYYY"
                        )
                      : ""}
                  </td>
                )}
              />
              <GridColumn
                field="place_of_supply_name"
                title="Place Of Supply"
                width={200}
              />
              <GridColumn
                field="gst"
                title="Total GST"
                width={200}
                cell={(props: any) => (
                  <td className="text-end">
                    {props.dataItem?.gst
                      ? formatIndianNumber(props.dataItem?.gst)
                      : null}
                  </td>
                )}
              />
              <GridColumn
                field="total_amount"
                title="Total Amount"
                width={200}
                cell={(props: any) => (
                  <td className="text-end">
                    {props.dataItem?.total_amount
                      ? formatIndianNumber(props.dataItem?.total_amount)
                      : null}
                  </td>
                )}
              />
            </Grid>
          </GridLayoutItem>
        </GridLayout>
      ) : (
        <AlertBox />
      )}
    </>
  );
};
export default PurchaseDueDateInvoiceReportForm;
